// Inferred-column extraction for subquery / derived-table aliases.
//
// `SELECT x.| FROM (SELECT ... ) x` — when the resolver hits the `x.`
// dotted-access slot it asks this helper for the projected column names
// of the inner SELECT. Best-in-class behavior across all 5 engines:
//
//   `SELECT *`            → expand to all columns of the inner FROM
//                           relation (looked up in `snapshotTables` /
//                           `cteBindings`).
//   `SELECT t.*`          → expand to all columns of `t` (alias-prefixed
//                           star). Resolution prefers an alias bound
//                           inside the subquery, falling back to a real
//                           table named `t` in the snapshot.
//   `SELECT col, col2`    → emit each bare identifier as its own column.
//   `SELECT t.col`        → emit `col` (dotted reference, last segment).
//   `SELECT expr AS name` → emit `name` (explicit alias wins).
//   `SELECT expr name`    → emit `name` (implicit alias — last bare
//                           identifier after the expression).
//   anything else         → skipped (anonymous expression — no
//                           inferable name).
//
// Designed to be engine-agnostic. The caller passes the engine for
// quoting awareness (the tokenizer already understands `[ ]`, `\`\``,
// `" "`). Heuristic-quality — not a full SQL parser; covers the 80/20
// cases users actually type in correlated-subquery / derived-table
// completion. Multi-statement / nested-subquery projection is handled
// only at the outermost SELECT of the body — nested subqueries inside
// the projection list are not unwrapped.

import type {
  CteBinding,
  DatabaseEngineId,
  OffsetRange,
} from '../../../core/engines';

import { tokenize, type Token } from '../context/tokenizer';

import { parseIdentifierToken } from './identifierUtils';
import { resolveCteColumns } from './resolveCteColumns';

export type InferSubqueryColumnsArgs = {
  readonly source: string;
  /**
   * The range covering the subquery / CTE body `( ... )` — start at the
   * opening paren, end at the closing paren INCLUSIVE. This paren-inclusive
   * invariant is required: `findOutermostSelect` locates the body's SELECT at
   * paren-depth 1, so callers MUST pass a paren-wrapped range (mirrors the
   * `AliasBinding.bodyRange` / `CteBinding.bodyRange` contract). A range that
   * starts inside the parens (at the SELECT itself) yields no projection.
   */
  readonly bodyRange: OffsetRange;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<InferTable>;
  readonly cteBindings: readonly CteBinding[];
  /** Folded names of CTEs currently being resolved on the recursion stack —
   *  threaded through to `resolveCteColumns` (via `pushAliasColumns`) so a
   *  self-/mutually-referential CTE does not stack-overflow. Defaults to a
   *  fresh empty Set at the top-level entry. */
  readonly visited?: ReadonlySet<string>;
};

export type InferTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType?: string }>;
};

/**
 * An inferred projected column. `dataType` is carried through when the
 * projection item resolves to a known snapshot column (a `*` / `alias.*`
 * expansion, or a `<alias>.<col>` / bare-`<col>` reference). It is undefined
 * for anonymous expressions and aliased expressions whose source column the
 * heuristic can't trace. Oracle uses it to resolve OBJECT-type attribute
 * access (`t.home_address.▮` where `home_address` is of type `ADDRESS_TYPE`).
 */
export type InferredColumn = { readonly name: string; readonly dataType?: string };

/** Projected column NAMES of a subquery body — the engine-agnostic default
 *  used by all five resolvers for `alias.|` dotted-access completion. */
export function inferSubqueryColumns(args: InferSubqueryColumnsArgs): string[] {
  return inferSubqueryProjection(args).map((c) => c.name);
}

/** Projected columns of a subquery body WITH their carried `dataType` where
 *  resolvable. Same parse as {@link inferSubqueryColumns}; used by the Oracle
 *  resolver to follow an OBJECT-typed projected column to its attributes. */
export function inferSubqueryProjection(args: InferSubqueryColumnsArgs): InferredColumn[] {
  const { source, bodyRange, engineId, snapshotTables, cteBindings } = args;
  const visited = args.visited ?? new Set<string>();
  const tokens = tokenize(source, bodyRange.start, bodyRange.end, engineId);
  if (tokens.length === 0) return [];

  // Locate the outermost SELECT inside the body. Skip leading `(`, `WITH`
  // (CTE preface), and any whitespace handled by the tokenizer already.
  const selectIdx = findOutermostSelect(tokens);
  if (selectIdx === -1) return [];

  // Locate the matching FROM at the same paren depth — the projection
  // list lives between `selectIdx + 1` and that FROM.
  const fromIdx = findMatchingFrom(tokens, selectIdx);
  const projectionEnd = fromIdx === -1 ? tokens.length : fromIdx;

  const projection = sliceProjectionItems(tokens, selectIdx + 1, projectionEnd);

  // Build a snapshot index of aliases bound INSIDE the subquery's FROM
  // so `SELECT t.*` can expand t's columns. Only single-relation
  // resolution is required — multi-join projections are tracked too.
  const innerAliases = fromIdx === -1 ? [] : collectInnerFromAliases(tokens, fromIdx + 1);

  const out: InferredColumn[] = [];
  const ctx: ExpandCtx = { source, engineId, snapshotTables, cteBindings, innerAliases, visited };
  for (const item of projection) {
    expandItem(item, out, ctx);
  }
  return out;
}

type ExpandCtx = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<InferTable>;
  readonly cteBindings: readonly CteBinding[];
  readonly innerAliases: readonly InnerAlias[];
  /** CTE-resolution cycle guard, threaded into `resolveCteColumns`. */
  readonly visited: ReadonlySet<string>;
};

// ───────────────────────────────────────────────────────────────────────────
// Projection slicing — split comma-separated items at paren-depth 0.
// ───────────────────────────────────────────────────────────────────────────

type ProjectionItem = readonly Token[];

function sliceProjectionItems(
  tokens: readonly Token[],
  from: number,
  to: number,
): ProjectionItem[] {
  const items: Token[][] = [[]];
  let depth = 0;
  for (let i = from; i < to; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'lparen') {
      depth += 1;
      items[items.length - 1].push(tok);
      continue;
    }
    if (tok.type === 'rparen') {
      depth -= 1;
      items[items.length - 1].push(tok);
      continue;
    }
    if (tok.type === 'comma' && depth === 0) {
      items.push([]);
      continue;
    }
    items[items.length - 1].push(tok);
  }
  return items.filter((arr) => arr.length > 0);
}

// ───────────────────────────────────────────────────────────────────────────
// Item → column-name(s)
// ───────────────────────────────────────────────────────────────────────────

type InnerAlias = {
  readonly alias: string;
  readonly relation: string;
  readonly schema?: string;
};

function expandItem(
  item: ProjectionItem,
  out: InferredColumn[],
  ctx: ExpandCtx,
): void {
  if (item.length === 0) return;

  // Case 1: bare `*`
  if (item.length === 1 && item[0].type === 'op' && item[0].text === '*') {
    expandStar(out, ctx);
    return;
  }

  // Case 2: `<ident>.*` (alias-prefixed star)
  if (
    item.length === 3 &&
    item[0].type === 'ident' &&
    item[1].type === 'dot' &&
    item[2].type === 'op' &&
    item[2].text === '*'
  ) {
    const aliasName = parseIdentifierToken(item[0].text).content;
    expandStarForAlias(aliasName, out, ctx);
    return;
  }

  // Case 3: explicit alias via `AS <name>`
  const asIdx = item.findIndex(
    (t) => t.type === 'kw' && t.text.toUpperCase() === 'AS',
  );
  if (asIdx !== -1 && asIdx + 1 < item.length) {
    const aliasTok = item[asIdx + 1];
    if (aliasTok.type === 'ident' || aliasTok.type === 'kw') {
      // The projected NAME is the alias; carry the source column's dataType
      // when the aliased expression is itself a simple `<a>.<col>` / `<col>`
      // reference (so `e.home_address AS addr` still tracks ADDRESS_TYPE).
      out.push({
        name: parseIdentifierToken(aliasTok.text).content,
        dataType: dataTypeOfExpr(item.slice(0, asIdx), ctx),
      });
      return;
    }
  }

  // Case 4: implicit alias `<expr> <name>` — the last two tokens are
  // identifiers (with optional dot in expr).
  const last = item[item.length - 1];
  const prev = item.length >= 2 ? item[item.length - 2] : null;
  if (
    item.length >= 2 &&
    last.type === 'ident' &&
    prev &&
    prev.type !== 'dot' &&
    prev.type !== 'kw'
  ) {
    // Heuristic: implicit alias when the projection has more than one
    // identifier and the preceding non-dot token is an ident/literal/op
    // (not a keyword like ORDER/BY etc).
    out.push({
      name: parseIdentifierToken(last.text).content,
      dataType: dataTypeOfExpr(item.slice(0, item.length - 1), ctx),
    });
    return;
  }

  // Case 5: bare column reference `<ident>` or `<ident>.<ident2>`
  if (last.type === 'ident') {
    out.push({
      name: parseIdentifierToken(last.text).content,
      dataType: dataTypeOfExpr(item, ctx),
    });
    return;
  }

  // Unknown / anonymous expression — no inferable name.
}

// ───────────────────────────────────────────────────────────────────────────
// Star expansion
// ───────────────────────────────────────────────────────────────────────────

function expandStar(out: InferredColumn[], ctx: ExpandCtx): void {
  for (const a of ctx.innerAliases) {
    pushAliasColumns(a, out, ctx);
  }
}

function expandStarForAlias(
  aliasName: string,
  out: InferredColumn[],
  ctx: ExpandCtx,
): void {
  const aliasFolded = aliasName.toLowerCase();
  const aliasMatch = ctx.innerAliases.find((a) => a.alias.toLowerCase() === aliasFolded);
  if (aliasMatch) {
    pushAliasColumns(aliasMatch, out, ctx);
    return;
  }
  // Fallback: treat the prefix as a direct relation name.
  pushAliasColumns({ alias: aliasName, relation: aliasName }, out, ctx);
}

function pushAliasColumns(a: InnerAlias, out: InferredColumn[], ctx: ExpandCtx): void {
  const relFolded = a.relation.toLowerCase();
  const schemaFolded = a.schema ? a.schema.toLowerCase() : null;

  const cte = ctx.cteBindings.find((c) => c.name.toLowerCase() === relFolded);
  if (cte) {
    const cteCols = resolveCteColumns({
      cte,
      source: ctx.source,
      engineId: ctx.engineId,
      snapshotTables: ctx.snapshotTables,
      cteBindings: ctx.cteBindings,
      visited: ctx.visited,
    });
    for (const c of cteCols) out.push({ name: c });
    return;
  }

  const tbl = ctx.snapshotTables.find(
    (t) =>
      t.name.toLowerCase() === relFolded &&
      (schemaFolded === null || (t.schema ?? '').toLowerCase() === schemaFolded),
  );
  if (!tbl) return;
  for (const c of tbl.columns) out.push({ name: c.name, dataType: c.dataType });
}

/**
 * Resolve the declared `dataType` of a projection expression that is a plain
 * column reference — `<col>` (resolved against the lone inner relation) or
 * `<alias>.<col>` (resolved against the named inner alias's relation). Returns
 * undefined for anything else (functions, arithmetic, literals) or when the
 * column isn't found in the snapshot. Used only to carry OBJECT/scalar type
 * info forward; never affects which names are projected.
 */
function dataTypeOfExpr(exprTokens: readonly Token[], ctx: ExpandCtx): string | undefined {
  const idents = exprTokens.filter((t) => t.type === 'ident' || t.type === 'dot');
  // Bare `<col>` — exactly one ident token, no dot.
  if (exprTokens.length === 1 && exprTokens[0].type === 'ident') {
    const col = parseIdentifierToken(exprTokens[0].text).content;
    // Resolve against the single inner relation (unambiguous bare column).
    if (ctx.innerAliases.length === 1) {
      return columnDataType(ctx.innerAliases[0], col, ctx);
    }
    return undefined;
  }
  // `<alias>.<col>` — ident dot ident.
  if (
    idents.length === 3 &&
    idents[0].type === 'ident' &&
    idents[1].type === 'dot' &&
    idents[2].type === 'ident' &&
    exprTokens.length === 3
  ) {
    const aliasName = parseIdentifierToken(idents[0].text).content.toLowerCase();
    const col = parseIdentifierToken(idents[2].text).content;
    const inner = ctx.innerAliases.find((a) => a.alias.toLowerCase() === aliasName);
    if (inner) return columnDataType(inner, col, ctx);
    // Fallback: treat the prefix as a direct relation name.
    return columnDataType({ alias: aliasName, relation: aliasName }, col, ctx);
  }
  return undefined;
}

function columnDataType(a: InnerAlias, colName: string, ctx: ExpandCtx): string | undefined {
  const relFolded = a.relation.toLowerCase();
  const schemaFolded = a.schema ? a.schema.toLowerCase() : null;
  const tbl = ctx.snapshotTables.find(
    (t) =>
      t.name.toLowerCase() === relFolded &&
      (schemaFolded === null || (t.schema ?? '').toLowerCase() === schemaFolded),
  );
  if (!tbl) return undefined;
  const colFolded = colName.toLowerCase();
  return tbl.columns.find((c) => c.name.toLowerCase() === colFolded)?.dataType;
}

// ───────────────────────────────────────────────────────────────────────────
// Token-level helpers
// ───────────────────────────────────────────────────────────────────────────

function findOutermostSelect(tokens: readonly Token[]): number {
  let depth = 0;
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth -= 1;
      continue;
    }
    // The body's leading `(` is one of the tokens — the outermost
    // SELECT sits at depth === 1.
    if (depth === 1 && tok.type === 'kw' && tok.text.toUpperCase() === 'SELECT') {
      return i;
    }
  }
  return -1;
}

function findMatchingFrom(tokens: readonly Token[], selectIdx: number): number {
  let depth = 1; // we're already past `SELECT` at depth 1 (body lparen + SELECT)
  for (let i = selectIdx + 1; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth -= 1;
      continue;
    }
    if (depth === 1 && tok.type === 'kw' && tok.text.toUpperCase() === 'FROM') {
      return i;
    }
  }
  return -1;
}

// Join-family / relation-introducer keywords that must NEVER be consumed as an
// implicit table alias. MySQL's `STRAIGHT_JOIN` is the load-bearing case: it is
// NOT in keywords.ts, so the tokenizer lexes it as `ident` — without this guard
// `FROM a x1 STRAIGHT_JOIN b y1` swallows `STRAIGHT_JOIN` as x1's alias and
// drops `b` from the inner aliases, so `SELECT *` misses b's columns. The rest
// (JOIN / INNER / LEFT / … / ON / USING and the clause boundaries) are listed
// for parity with the sibling scanners (`extractQueryStructure.from.ts`,
// `aliasScope.scanner.ts`, `findReferencedRelations.ts`); most lex as `kw` and
// are already excluded by the branch's `type === 'ident'` check, but listing
// them keeps the guard correct regardless of how a given engine lexes them.
const ALIAS_EXCLUDED: ReadonlySet<string> = new Set([
  'JOIN', 'STRAIGHT_JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS',
  'OUTER', 'NATURAL', 'LATERAL', 'APPLY', 'ON', 'USING',
  'WHERE', 'GROUP', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET',
  'RETURNING', 'UNION', 'INTERSECT', 'EXCEPT',
]);

function collectInnerFromAliases(
  tokens: readonly Token[],
  startIdx: number,
): InnerAlias[] {
  const out: InnerAlias[] = [];
  let i = startIdx;
  let depth = 1;
  // Stop at the SELECT body's enclosing rparen OR at a clause boundary
  // keyword (WHERE / GROUP / ORDER / HAVING / etc.).
  const STOP: ReadonlySet<string> = new Set([
    'WHERE', 'GROUP', 'HAVING', 'ORDER', 'LIMIT', 'OFFSET',
    'RETURNING', 'UNION', 'INTERSECT', 'EXCEPT',
  ]);
  while (i < tokens.length && depth > 0) {
    const tok = tokens[i];
    if (tok.type === 'lparen') {
      depth += 1;
      i += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth -= 1;
      i += 1;
      continue;
    }
    if (depth === 1 && tok.type === 'kw' && STOP.has(tok.text.toUpperCase())) {
      break;
    }
    // A join-family / relation-introducer keyword the tokenizer lexed as
    // `ident` (notably MySQL STRAIGHT_JOIN, absent from keywords.ts) must NOT
    // be parsed as a relation — doing so consumes the REAL next relation as its
    // implicit alias and drops it from `innerAliases`, so `SELECT *` misses
    // that relation's columns. Skip it so the next relation is read on its own.
    if (depth === 1 && tok.type === 'ident' && ALIAS_EXCLUDED.has(tok.text.toUpperCase())) {
      i += 1;
      continue;
    }
    if (depth === 1 && tok.type === 'ident') {
      // Parse a relation reference: <ident>[.<ident2>][.<ident3>] [[AS] <alias>]
      const parts: string[] = [parseIdentifierToken(tok.text).content];
      let j = i + 1;
      while (j < tokens.length && tokens[j].type === 'dot') {
        const next = tokens[j + 1];
        if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
        parts.push(parseIdentifierToken(next.text).content);
        j += 2;
      }
      let alias = parts[parts.length - 1];
      let lastIdx = j - 1;
      if (j < tokens.length && tokens[j].type === 'kw' && tokens[j].text.toUpperCase() === 'AS') {
        if (tokens[j + 1] && tokens[j + 1].type === 'ident') {
          alias = parseIdentifierToken(tokens[j + 1].text).content;
          lastIdx = j + 1;
        }
      } else if (
        j < tokens.length &&
        tokens[j].type === 'ident' &&
        !ALIAS_EXCLUDED.has(tokens[j].text.toUpperCase())
      ) {
        // Implicit alias `<relation> <alias>` — but NOT when the next ident is
        // a join-family / relation-introducer keyword the tokenizer happens to
        // lex as `ident` (e.g. MySQL STRAIGHT_JOIN). Treating it as an alias
        // would swallow the keyword and drop the real next relation.
        alias = parseIdentifierToken(tokens[j].text).content;
        lastIdx = j;
      }
      out.push({
        alias,
        relation: parts[parts.length - 1],
        ...(parts.length >= 2 ? { schema: parts[parts.length - 2] } : {}),
      });
      i = lastIdx + 1;
      continue;
    }
    i += 1;
  }
  return out;
}
